/**
 * The studio shelves read back into spec axes — the inverse of `studioApplySpec`. The shelves are the
 * source of truth at save time, and each deck's arrangement is bundled into `deck_overrides` so one
 * write keeps every deck's selections.
 */

import type { NestedRowLayout, ReportSpec, ReportFilter, RowAxis } from 'src/components/star-grid/api/reportEngineTypes';
import type {
  StudioBinding,
  StudioDataset,
  StudioDeck,
  StudioField,
  StudioLineEntry,
  StudioLineSet
} from 'src/components/star-grid/reportStudio.types';
import { normalizeDimension } from 'src/components/star-grid/utils/studioApplySpec';
import { DEFAULT_ROW_SELECTOR } from 'src/components/star-grid/utils/reportSpec';
import { expandableMembersExplicit, expandDefaultOf, lineMember, reportOmittedLineIds } from 'src/components/star-grid/utils/lineSetExpansion';

/** Rows + filters as the spec states them, read off one deck's shelves. */
export interface DeckAxes {
  readonly row_axis?: ReportSpec['row_axis'];
  readonly filters: ReportFilter[];
}

const bindingOf = (dataset: StudioDataset, field: StudioField) => dataset.bindings.find((binding) => binding.id === field.binding_id);

/** Explicit members of a shelf selection; `all` contributes none, which is "unrestricted". */
const membersOf = (field: StudioField): string[] => (field.selection?.kind === 'all' ? [] : [...(field.selection?.members ?? [])]);

/** A line's binding: its own, else (as the type states) its set's. */
const lineBinding = (dataset: StudioDataset, entry: StudioLineEntry, set?: StudioLineSet): StudioBinding | undefined => {
  const id = entry.binding_id ?? set?.binding_id;
  return id ? dataset.bindings.find((item) => item.id === id) : undefined;
};

/** A binding id's normalised dimension; an id the dataset does not know stands for itself. */
const rowDimensionOf = (dataset: StudioDataset, id: string): string => {
  const binding = dataset.bindings.find((item) => item.id === id);
  return binding ? normalizeDimension(binding.dimension) : id;
};

/** The second dimension: a later rows field on another dimension, else a line on another dimension whose field is gone. */
const innerRowBinding = (
  deck: StudioDeck,
  dataset: StudioDataset,
  outer: StudioBinding,
  lines: readonly StudioLineEntry[],
  set?: StudioLineSet
): StudioBinding | undefined => {
  const other = (binding?: StudioBinding) => Boolean(binding && normalizeDimension(binding.dimension) !== normalizeDimension(outer.dimension));
  return (
    deck.rows
      .slice(1)
      .map((field) => bindingOf(dataset, field))
      .find(other) ?? lines.map((entry) => lineBinding(dataset, entry, set)).find(other)
  );
};

/** The rows shelf as a row axis: lines on the second dimension become `nested`; `readOnlyNested` is carried through. */
const rowAxisFromShelves = (
  deck: StudioDeck,
  dataset: StudioDataset,
  readOnlyNested?: RowAxis,
  nestedLayout: NestedRowLayout = 'stack'
): RowAxis | undefined => {
  const outerField = deck.rows[0];
  const outer = outerField ? bindingOf(dataset, outerField) : undefined;
  if (!outer) return undefined;
  // The LINE SET is the row member list once the rows field points at one, so it — not the spec's
  // imported selector — states what the report shows. Without this the preview kept rendering the
  // members the import shipped: adding, removing or reordering a line changed nothing on screen.
  const lineSet = deck.line_sets.find((set) => deck.rows.some((row) => row.selection.reference_id === set.id));
  const omitted = lineSet ? reportOmittedLineIds(lineSet) : new Set<string>();
  const lines = (lineSet?.entries ?? []).filter((entry) => entry.kind !== 'spacer' && !omitted.has(entry.id));
  const inner = innerRowBinding(deck, dataset, outer, lines, lineSet);
  // A line's own binding decides its axis: a member of another dimension on the first axis matches nothing.
  const dimensionOf = (entry: StudioLineEntry): string => normalizeDimension(lineBinding(dataset, entry, lineSet)?.dimension ?? outer.dimension);
  const onOuter = (entry: StudioLineEntry) => dimensionOf(entry) === normalizeDimension(outer.dimension);
  const onInner = (entry: StudioLineEntry) => Boolean(inner) && dimensionOf(entry) === normalizeDimension(inner?.dimension ?? '');
  const outerMembers = lines.filter(onOuter).map(lineMember).filter(Boolean);
  const innerMembers = lines.filter(onInner).map(lineMember).filter(Boolean);
  // Each axis states the set's expansion for its own lines, so the second dimension's nodes keep theirs.
  // expand_default is stated as a concrete boolean whenever a set exists -- never left for the server
  // to default -- so the report opens exactly as the Studio's carets read. No set
  // at all states nothing, so a saved report's old expansion cannot outlive its lines.
  const expansion = (members: readonly string[]) =>
    lineSet
      ? {
          expand_default: expandDefaultOf(lineSet),
          expanded_members: (lineSet.expanded_members ?? []).filter((member) => members.includes(member)),
          collapsed_members: (lineSet.collapsed_members ?? []).filter((member) => members.includes(member)),
          // An explicit list restricts which rows expand; null leaves it derived for the engine.
          expandable_members: expandableMembersExplicit(lineSet)
            ? (lineSet.expandable_members ?? []).filter((member) => members.includes(member))
            : null
        }
      : { expand_default: undefined, expanded_members: undefined, collapsed_members: undefined, expandable_members: undefined };
  const nested: RowAxis | undefined = inner
    ? {
        dimension: inner.dimension,
        hierarchy: inner.hierarchy,
        // No picked lines means "All members", the same default the first dimension starts from.
        selector: innerMembers.length > 0 ? { kind: 'mdx_elements', elements: innerMembers } : DEFAULT_ROW_SELECTOR,
        ...expansion(innerMembers)
      }
    : readOnlyNested;
  return {
    dimension: outer.dimension,
    hierarchy: outer.hierarchy,
    // Stated only when the line set has members; otherwise the spec's own selector stands, so an
    // empty or spacer-only set cannot blank a report that was rendering fine.
    selector: (outerMembers.length > 0 ? { kind: 'mdx_elements', elements: outerMembers } : undefined) as never,
    ...expansion(outerMembers),
    // Always stated, so removing the second dimension from the shelf removes it from the report too.
    nested,
    nested_layout: nested ? nestedLayout : undefined
  };
};

/** Whether two binding ids name the same dimension; an id the dataset does not know only matches itself. */
export const sameRowDimension = (dataset: StudioDataset, left: string, right: string): boolean =>
  rowDimensionOf(dataset, left) === rowDimensionOf(dataset, right);

/** A saved row axis's second-dimension layout: as stated, else `cross`, the only layout before it was stated. */
export const savedNestedLayout = (axis: Pick<RowAxis, 'nested' | 'nested_layout'> | undefined): NestedRowLayout =>
  axis?.nested ? axis.nested_layout ?? 'cross' : 'stack';

/** Rows support two dimensions: the first, and one `nested` second level. */
export const MAX_ROW_DIMENSIONS = 2;

/** True when putting `bindingId` on the rows would add a dimension beyond `MAX_ROW_DIMENSIONS`. */
export const addsRowDimensionBeyondLimit = (deck: StudioDeck, dataset: StudioDataset, bindingId: string, readOnlyNested?: RowAxis): boolean => {
  const dimensionOf = (id: string): string => rowDimensionOf(dataset, id);
  // A read-only second dimension is still one of the two: adding another would silently replace it on save.
  const dimensions = new Set([
    ...deck.rows.map((field) => dimensionOf(field.binding_id)),
    ...(readOnlyNested ? [normalizeDimension(readOnlyNested.dimension)] : [])
  ]);
  return !dimensions.has(dimensionOf(bindingId)) && dimensions.size >= MAX_ROW_DIMENSIONS;
};

export const deckAxesFromShelves = (deck: StudioDeck, dataset: StudioDataset, readOnlyNested?: RowAxis, nestedLayout?: NestedRowLayout): DeckAxes => {
  const filters = deck.filters
    .map((field) => ({ field, binding: bindingOf(dataset, field) }))
    .filter((entry): entry is { field: StudioField; binding: NonNullable<typeof entry.binding> } => Boolean(entry.binding))
    .map(({ field, binding }) => ({
      dimension: binding.dimension,
      hierarchy: binding.hierarchy,
      members: membersOf(field)
    }))
    // A filter with no members restricts nothing; sending it only produced an empty chip on reload.
    .filter((filter) => filter.members.length > 0);
  return { row_axis: rowAxisFromShelves(deck, dataset, readOnlyNested, nestedLayout), filters };
};

/**
 * `spec` with the active deck's shelves as the defaults and every other deck's as an override.
 *
 * `perDeck` holds the arrangement last seen on each deck. The active deck is read live because its
 * shelves may have changed since the last deck switch.
 */
export const specWithDeckAxes = (
  spec: ReportSpec,
  activeDeck: string,
  activeAxes: DeckAxes,
  perDeck: Readonly<Record<string, DeckAxes>>
): ReportSpec => {
  const overrides: Record<string, { row_axis?: unknown; filters?: ReportFilter[] }> = {};
  for (const [deck, axes] of Object.entries(perDeck)) {
    if (deck === activeDeck) continue;
    overrides[deck] = {
      // The shelves' selector wins when they state one (a line set), else the spec's stands.
      ...(axes.row_axis ? { row_axis: { ...spec.row_axis, ...axes.row_axis, selector: axes.row_axis.selector ?? spec.row_axis?.selector } } : {}),
      filters: axes.filters
    };
  }
  return {
    ...spec,
    row_axis: activeAxes.row_axis
      ? { ...spec.row_axis, ...activeAxes.row_axis, selector: activeAxes.row_axis.selector ?? spec.row_axis?.selector }
      : spec.row_axis,
    filters: activeAxes.filters,
    deck_overrides: overrides
  };
};
